import { faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { faCog, faEraser, faExclamationTriangle, faLifeRing, faPencil, faUndo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
    BLOG_REPORT_ARTICLE,
    CHANGE_ARTICLE,
    DELETE_ARTICLE,
    REMOVE_FROM_FEED,
    RETURN_TO_FEED,
    SEO_TITLE_BLOGS_article,
    SHOW_USERS_WHO_LIKED_THIS_POST,
} from 'mk/autogenerated/translations/ArticlePage.3a9e8d84bb70d6e4da758cc12523510e'
import {
    canManageForum, hasAds,
    isAuthenticated,
    isAuthenticatedAs,
} from 'mk/bazaar/common/userUtils';
import { adminclub_blogs_post_url } from 'mk/urls/functions';
import { analyticsStoreEvent } from 'mk2/actions';
import Article from 'mk2/apps/blogs/containers/Article/Article';
import {
    articleBookmarkTrigger,
    articleDeleteTrigger,
    articleFetchTrigger,
    articlePageViewTrigger,
} from 'mk2/apps/blogs/containers/Article/Article.actions';
import { BLOGS_ARTICLE_MENU_MORE } from 'mk2/apps/blogs/containers/Article/Article.constants';
import { getArticleTitleAsString } from 'mk2/apps/blogs/containers/Article/Article.helpers';
import articlesReducer, { ArticleState } from 'mk2/apps/blogs/containers/Article/Article.reducers';
import articlesSaga from 'mk2/apps/blogs/containers/Article/Article.sagas';
import { getArticleState } from 'mk2/apps/blogs/containers/Article/Article.selectors';
import { blogsSetDisplayingAtFeedTrigger } from 'mk2/apps/blogs/containers/BlogsShared/BlogsShared.actions';
import blogsSharedSaga from 'mk2/apps/blogs/containers/BlogsShared/BlogsShared.sagas';
import commentsSaga from 'mk2/apps/blogs/containers/Comments/Comments.sagas';
import { getCommentIdsOrNull, getPostsState } from 'mk2/apps/blogs/containers/Comments/Comments.selectors';
import { getPostModel } from 'mk2/apps/blogs/helpers';
import { articleBlogs, articleBlogsOG } from 'mk2/apps/blogs/structuredData';
import {
    blogsEntityLikersUrl,
    blogsProfileUrl,
    blogsUrl,
    editAdvancedArticleUrl,
} from 'mk2/apps/blogs/urls';
import { articleUrl } from 'mk2/apps/blogs/utils';
import { getCommentComplaintsCounts } from 'mk2/apps/groups/containers/GroupShared/GroupShared.selectors';
import type { UtmQuery } from 'mk2/apps/home/schemas';
import { MenuItem } from 'mk2/components/menu/MenuItem';
import { Btn, BtnType } from 'mk2/components/Btn';
import { AppName } from 'mk2/containers/Application/Application';
import { complaintOpenTrigger } from 'mk2/containers/Complaint/Complaint.actions';
import { ComplaintTargetType } from 'mk2/containers/Complaint/Complaint.schemas';
import { Menu } from 'mk2/containers/Menu/Menu';
import { menuToggle } from 'mk2/containers/Menu/Menu.actions';
import { memoizeOne } from 'mk2/helpers/cache';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { removeEmpty, url } from 'mk2/helpers/urls';
import Page, { AnchorLeaderboardProps, BrandingProps, HalfpageProps, RightContentSide } from 'mk2/pages/Page';
import {
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    AppState,
} from 'mk2/reducers';
import { LoadingState } from 'mk2/reducers/loadingState';
import {
    AdvancedArticleBookmarkEntity, AdvancedArticleBookmarkSchema,
    AdvancedArticleListItemDetailEntity, AdvancedArticleListItemDetailSchema,
    BlogPostEntity,
    BlogPostSchema,
    PhotoEntity,
    PhotoSchema,
    UserEntity,
} from 'mk2/schemas';
import { getDenormalizedEntities, getDenormalizedEntitiesMap, getDenormalizedEntity } from 'mk2/selectors';
import { HistoryLocationState } from 'mk2/services/browserHistory';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import { parse } from 'query-string';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps, StaticContext } from 'react-router';

const fasCogLg: FontAwesomeIconProps = { icon: faCog, size: 'lg' };

export type QueryParams = UtmQuery & {
    key?: string;
};

export interface OwnProps {
}

export interface StateProps {
    articlePost: BlogPostEntity;
    advancedArticleListItemDetails: AdvancedArticleListItemDetailEntity[];
    baseUrl: string;
    bookmarked?: boolean;
    commentsCount: number;
    complaintsCount?: number;
    errorMessage: string;
    groupModerators?: UserEntity[];
    isMobile: boolean;
    query: QueryParams;
    loadingState: LoadingState;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    showAds: boolean;
    suggestedArticles: BlogPostEntity[];
    suggestedArticlesHomepagePhotos: {[articleId: number]: PhotoEntity};
}

export interface DispatchProps {
    onLoad(username: string, articleSlug: string, articleOpened: boolean, query: QueryParams, accessKey?: string);
    onBlogsArticleToggleMenuMore();
    onDelete(articleId: number, redirectUrl?: string);
    onBookmark(postId: number, username: string, articleSlug: string, bookmarked: boolean);
    onPageView(username: string, articleSlug: string);
    onReport(entityType: ComplaintTargetType, postId: number);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onStoreEvent(name: string, props: any);
}

export type RouteProps = RouteComponentProps<{
    username: string;
    articleSlug: string;
}, StaticContext, HistoryLocationState>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ArticlePage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {

    private parseQuery = memoizeOne((qs: string): QueryParams => {
        // parse and sanitize input
        return removeEmpty(parse(qs));
    });

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const { username, articleSlug } = ownProps.match.params;
            const query = this.parseQuery(ownProps.location.search);

            const articleState: ArticleState = getArticleState(state, username, articleSlug, false);
            const articlePost: BlogPostEntity = getDenormalizedEntity<BlogPostEntity>(
                state, BlogPostSchema, articleState.postId,
            );
            const bookmark: AdvancedArticleBookmarkEntity = getDenormalizedEntity<AdvancedArticleBookmarkEntity>(
                state, AdvancedArticleBookmarkSchema, articleState.bookmarkId,
            );

            const advancedArticleListItemDetails = getDenormalizedEntities<AdvancedArticleListItemDetailEntity>(
                state, AdvancedArticleListItemDetailSchema, articleState.advancedArticleListItemDetails,
            );

            let commentsCount = 0;
            if (articlePost) {
                const key = `${getPostModel(articlePost)}/${articlePost.id}`;
                const commentIdsOrNull = getCommentIdsOrNull(getPostsState(state), key);
                commentsCount = commentIdsOrNull ? commentIdsOrNull.length : commentsCount;
            }

            const permissions = getRequestPermissions(state);

            const isAdmin = permissions.indexOf('forum.can_manage_forum') >= 0;

            const complaintsCount = (isAdmin) && articlePost
                ? getCommentComplaintsCounts(state, [articlePost.id], LoadingState.SUCCESS)[articlePost.id]
                : 0;

            const suggestedArticles = getDenormalizedEntities<BlogPostEntity>(
                state, BlogPostSchema, articleState.suggestedArticleIds,
            );

            const suggestedArticlesHomepagePhotos = getDenormalizedEntitiesMap<PhotoEntity>(
                state, PhotoSchema, articleState.suggestedArticlesHomepagePhotos,
            );

            const showAds = articlePost && !articlePost.isPRArticle && !articlePost.isActiveAmbassador && !articlePost.isNoAds;

            const bookmarked = Boolean(bookmark);

            return {
                articlePost,
                advancedArticleListItemDetails,
                baseUrl: getRequestBaseUrl(state),
                bookmarked,
                commentsCount,
                complaintsCount,
                errorMessage: articleState.errorMessage,
                isMobile: getRequestDeviceMobile(state),
                loadingState: articleState.loadingState,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                showAds,
                suggestedArticles,
                suggestedArticlesHomepagePhotos,
                query,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: articleFetchTrigger,
            onBlogsArticleToggleMenuMore: () => menuToggle(BLOGS_ARTICLE_MENU_MORE),
            onDelete: articleDeleteTrigger,
            onBookmark: articleBookmarkTrigger,
            onPageView: articlePageViewTrigger,
            onReport: complaintOpenTrigger,
            onSetDisplayingAtFeed: blogsSetDisplayingAtFeedTrigger,
            onStoreEvent: analyticsStoreEvent,
        };
    }

    public getMetaTitle(props: Props): string {
        const { articlePost } = props;
        return articlePost
            ? interpolate(SEO_TITLE_BLOGS_article, { article: getArticleTitleAsString(articlePost.titleAST) || ''})
            : null;
    }

    public getStructuredData(props: Props): object[] {
        const { articlePost, baseUrl } = props;
        if (articlePost) {
            return [
                articleBlogs(baseUrl, articlePost),
            ];
        }
    }

    public getOpenGraphData(props: Props, baseUrl: string, currentUrl: string): {[key: string]: string} {
        const { articlePost } = props;
        if (articlePost) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...articleBlogsOG(baseUrl, articlePost),
            };
        }
    }

    public getCanonicalUrl(baseUrl: string, props: Props): string {
        const { articlePost } = props;
        if (articlePost) {
            return baseUrl + articleUrl(articlePost);
        }
    }

    public render(props: Props): false | JSX.Element {
        return (
            <Article {...props} />
        );
    }

    public renderRight(props: Props): JSX.Element {
        return null;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return blogsUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { articlePost, showAds } = props;
        return articlePost && showAds ? {
            zone: 'Blogs Article - Halfpage Sticky (Desktop)',
        } : null;
    }

    public getBrandingProps(props: Props): BrandingProps {
        const { articlePost, showAds } = props;

        if (!articlePost || !showAds) {
            return null;
        }

        return {
            zone: 'Blogs - Branding',
        };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { articlePost, requestUser, showAds, isMobile } = props;

        if (!isMobile || requestUser && !hasAds(requestUser)) {
            return null;
        }

        if (!articlePost || !showAds) {
            return null;
        }

        return {
            zone: 'Blogs Article - Anchor Ad Leaderboard (Mobile)',
        };
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const {
            onBlogsArticleToggleMenuMore, onDelete, onReport, onSetDisplayingAtFeed, articlePost,
            requestUser, requestUserPermissions,
        } = props;
        // const { username, articleSlug } = props.match.params;

        if (!articlePost || articlePost.deletedAt) {
            return null;
        }

        const isAuthor = articlePost && isAuthenticatedAs(requestUser, articlePost.user);
        const isAdmin = requestUserPermissions && canManageForum(requestUserPermissions);
        const isAmbassadorAllowedToEdit = articlePost.ambassadorBrandId
            && requestUser.isAmbassadorOfBrandId === articlePost.ambassadorBrandId;

        const showWhoLiked = isAuthenticated(requestUser) && articlePost.likeable.likesCount > 0;
        const showReport = true;
        const showSetDisplayingAtFeed = isAdmin;

        let menu;
        menu = (
            <Menu
                menuId={BLOGS_ARTICLE_MENU_MORE}
                trigger={
                    <Btn
                        type={BtnType.BlueText}
                        icon={fasCogLg}
                        onClick={onBlogsArticleToggleMenuMore}
                    />
                }
            >
                {(isAdmin || isAuthor || isAmbassadorAllowedToEdit) && (
                    <MenuItem
                        type={BtnType.Blue}
                        label={CHANGE_ARTICLE}
                        icon={faPencil}
                        link={editAdvancedArticleUrl(articlePost)}
                        isAdminUI={isAdmin && !isAuthor}
                    />
                )}
                {showReport && (
                    <MenuItem
                        label={BLOG_REPORT_ARTICLE}
                        icon={faExclamationTriangle}
                        onClick={this.handleOnReport(onReport, articlePost.id)}
                    />
                )}
                {showWhoLiked && (
                    <MenuItem
                        label={SHOW_USERS_WHO_LIKED_THIS_POST}
                        icon={faThumbsUp}
                        link={url(blogsEntityLikersUrl, {type: 'article', entityId: articlePost.id})}
                    />
                )}
                {(isAuthor || isAdmin) && (
                    <MenuItem
                        label={DELETE_ARTICLE}
                        icon={faLifeRing}
                        onClick={this.handleOnDelete(onDelete, articlePost)}
                        isAdminUI={isAdmin && !isAuthor}
                    />
                )}
                {isAdmin && (
                    <MenuItem
                        label={'Adminclub'}
                        icon={faLifeRing}
                        link={adminclub_blogs_post_url(articlePost.id)}
                        asAnchor
                        isAdminUI
                    />
                )}
                {showSetDisplayingAtFeed && (
                    <MenuItem
                        label={articlePost.isDisplayingAtFeed ? REMOVE_FROM_FEED : RETURN_TO_FEED}
                        icon={articlePost.isDisplayingAtFeed ? faEraser : faUndo}
                        onClick={
                            articlePost.isDisplayingAtFeed
                                ? this.handleRemoveFromFeed(onSetDisplayingAtFeed, articlePost.id)
                                : this.handleReturnToFeed(onSetDisplayingAtFeed, articlePost.id)
                        }
                        isAdminUI={isAdmin && !isAuthor}
                    />
                )}
            </Menu>
        );

        return menu;
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.blogs.blogsArticles', articlesReducer);
        store.injectSaga('containers.blogs.blogsArticles', articlesSaga);

        store.injectSaga('containers.blogs.blogsShared', blogsSharedSaga);

        store.injectSaga('containers.blogs.shared.comments', commentsSaga);
    }

    private handleRemoveFromFeed = (onSetDisplayingAtFeed, articleId) => () => {
        onSetDisplayingAtFeed(articleId, false);
    };

    private handleReturnToFeed = (onSetDisplayingAtFeed, articleId) => () => {
        onSetDisplayingAtFeed(articleId, true);
    };

    private handleOnReport = (onReport, postId) => () => {
        onReport(ComplaintTargetType.PhotoblogPost, postId);
    };

    private handleOnDelete = (onDelete, post) => () => {
        const redirectUrl = url(blogsProfileUrl, { username: post.user.username });
        onDelete(post.id, redirectUrl);
    };
}
